import type { ReactElement } from "react";
export type ConceptId = "redakcja" | "manifest" | "dokument";

export type Concept = {
  id: ConceptId;
  name: string;
  tagline: string;
  modern: string;
  materials: string;
  risk: string;
  Preview: (props: { device: "desktop" | "mobile" }) => ReactElement;
};

function PhotoSlot({ label, className = "" }: { label: string; className?: string }) {
  return (
    <div
      role="img"
      aria-label={label}
      className={`flex items-end bg-steel-soft p-4 text-[0.65rem] uppercase tracking-[0.18em] text-coral-text ${className}`}
    >
      {label}
    </div>
  );
}

function Redakcja({ device }: { device: "desktop" | "mobile" }) {
  const desktop = device === "desktop";
  return (
    <div className="min-h-full bg-mist text-ink">
      <header className="flex items-center justify-between border-b border-steel px-6 py-4 md:px-10">
        <span className="text-sm font-semibold tracking-[0.2em] uppercase">Sylwia Sitkowska</span>
        {desktop && (
          <nav className="flex gap-7 text-xs uppercase tracking-[0.14em] text-coral-text">
            <span>Mastermind</span>
            <span>O mnie</span>
            <span>Wystąpienia</span>
            <span>Media</span>
          </nav>
        )}
        <span className="border border-coral px-4 py-2 text-xs uppercase tracking-[0.14em] text-coral-text">
          Szukasz prelegentki?
        </span>
      </header>

      <div className={`grid ${desktop ? "grid-cols-[1.15fr_0.85fr]" : "grid-cols-1"}`}>
        <div className="border-b border-steel px-6 py-12 md:border-b-0 md:border-r md:px-10 md:py-16">
          <p className="text-[0.65rem] uppercase tracking-[0.22em] text-coral-text">Wydanie 01, Psychologia biznesu</p>
          <h1 className={`mt-8 font-semibold leading-[0.95] tracking-[-0.03em] ${desktop ? "text-[4.2rem]" : "text-[2.6rem]"}`}>
            Nie trać siebie,
            <br />
            <span className="text-coral-text">budując sukces.</span>
          </h1>
          <div className="mt-10 h-px w-full bg-steel" />
          <div className={`mt-8 gap-8 ${desktop ? "grid grid-cols-2" : "space-y-6"}`}>
            <p className="text-sm leading-relaxed text-ink/80">
              Twoja firma jest wypadkową tego, jak myślisz, reagujesz i podejmujesz decyzje.
            </p>
            <p className="text-sm leading-relaxed text-ink/80">
              Pomagam liderom rozwijać biznes bez wypalenia i utraty życia poza pracą.
            </p>
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            <span className="bg-ink px-6 py-3 text-sm text-surface">Poznaj mastermind</span>
            <span className="border border-coral px-6 py-3 text-sm text-coral-text">Posłuchaj rozmowy</span>
          </div>
        </div>
        <PhotoSlot
          label="Portret redakcyjny 4:5, naturalne światło"
          className={desktop ? "min-h-[34rem]" : "min-h-[22rem]"}
        />
      </div>

      <div className="grid grid-cols-3 border-t border-steel text-center text-xs uppercase tracking-[0.14em] text-coral-text">
        <div className="border-r border-steel px-4 py-6">30 placówek</div>
        <div className="border-r border-steel px-4 py-6">300 osób</div>
        <div className="px-4 py-6">20 lat praktyki</div>
      </div>
    </div>
  );
}

function Manifest({ device }: { device: "desktop" | "mobile" }) {
  const desktop = device === "desktop";
  return (
    <div className="min-h-full bg-ink text-mist">
      <header className="flex items-center justify-between px-6 py-5 md:px-12">
        <span className="text-sm font-semibold tracking-[0.2em] uppercase">S.S.</span>
        <span className="rounded-full bg-coral px-5 py-2 text-xs uppercase tracking-[0.14em]">Szukasz prelegentki?</span>
      </header>

      <section className={`px-6 md:px-12 ${desktop ? "pt-14 pb-20" : "pt-8 pb-12"}`}>
        <h1
          className={`font-semibold uppercase leading-[0.86] tracking-[-0.04em] ${
            desktop ? "text-[7rem]" : "text-[3.2rem]"
          }`}
        >
          Nie trać
          <br />
          <span className="text-coral-text">siebie</span>
          <br />
          budując
          <br />
          sukces
        </h1>

        <div className={`mt-12 gap-10 ${desktop ? "grid grid-cols-[0.9fr_1.1fr] items-end" : "space-y-8"}`}>
          <p className="max-w-md text-base leading-relaxed text-mist/70">
            Psycholog dla liderów. Psychoterapeutka. Przedsiębiorczyni. Pracuję z właścicielami firm nad
            spokojniejszymi decyzjami i biznesem, który nie zjada życia.
          </p>
          <PhotoSlot
            label="Kadr filmowy 16:9, ruch, ziarno, kontrast"
            className={`bg-coral/25 text-mist ${desktop ? "min-h-[16rem]" : "min-h-[12rem]"}`}
          />
        </div>
      </section>

      <div className="overflow-hidden border-y border-mist/15 py-4">
        <p className="whitespace-nowrap text-xs uppercase tracking-[0.4em] text-mist/50">
          Mastermind · Konsultacje · Wystąpienia · Media · Książki · Mastermind · Konsultacje
        </p>
      </div>
    </div>
  );
}

function Dokument({ device }: { device: "desktop" | "mobile" }) {
  const desktop = device === "desktop";
  return (
    <div className="min-h-full bg-surface text-ink">
      <div className="relative">
        <PhotoSlot
          label="Pełnoekranowy film dokumentalny, Sylwia w pracy"
          className={`w-full bg-ink/85 text-mist ${desktop ? "min-h-[32rem]" : "min-h-[26rem]"}`}
        />
        <div className="absolute inset-0 flex flex-col justify-between p-6 md:p-12">
          <div className="flex items-center justify-between text-mist">
            <span className="text-sm font-semibold tracking-[0.2em] uppercase">Sylwia Sitkowska</span>
            <span className="border border-mist/60 px-4 py-2 text-xs uppercase tracking-[0.14em]">
              Szukasz prelegentki?
            </span>
          </div>
          <div>
            <p className="text-[0.65rem] uppercase tracking-[0.22em] text-mist/70">Psychologia · Biznes · Przywództwo</p>
            <h1
              className={`mt-4 max-w-3xl font-semibold leading-[1.02] tracking-[-0.03em] text-mist ${
                desktop ? "text-[3.6rem]" : "text-[2.2rem]"
              }`}
            >
              Nie trać siebie, budując sukces.
            </h1>
            <div className="mt-8 flex flex-wrap gap-3">
              <span className="bg-coral px-6 py-3 text-sm text-mist">Poznaj mastermind</span>
              <span className="border border-mist/60 px-6 py-3 text-sm text-mist">Obejrzy nagranie</span>
            </div>
          </div>
        </div>
      </div>

      <section className={`px-6 py-12 md:px-12 md:py-16 ${desktop ? "grid grid-cols-[0.8fr_1.2fr] gap-16" : ""}`}>
        <p className="text-[0.65rem] uppercase tracking-[0.22em] text-coral-text">Dla kogo</p>
        <div className={`gap-10 ${desktop ? "grid grid-cols-2" : "mt-6 space-y-8"}`}>
          <p className="text-lg leading-relaxed">
            Dla właścicieli firm, którzy czują, że rozwój dzieje się kosztem ich zdrowia i relacji.
          </p>
          <p className="text-sm leading-relaxed text-ink-soft">
            Pracuję indywidualnie i w grupach. Mastermind to program, w którym psychologia spotyka
            realne decyzje biznesowe.
          </p>
        </div>
      </section>
    </div>
  );
}

export const concepts: Concept[] = [
  {
    id: "redakcja",
    name: "Redakcja",
    tagline: "Magazynowy układ, hairline'y, typografia wydawnicza",
    modern: "Siatka jak w magazynie zamiast kart; cienkie linie, duża typografia, brak zaokrągleń.",
    materials: "Portret 4:5 w naturalnym świetle, 3-5 kadrów reportażowych z pracy.",
    risk: "Bez mocnych zdjęć może wyglądać sucho i „papierowo”.",
    Preview: Redakcja,
  },
  {
    id: "manifest",
    name: "Manifest",
    tagline: "Ciemne tło, wielka typografia, śliwkowy akcent",
    modern: "Odważna skala typografii i kontrast; strona wygląda jak wypowiedź, nie jak oferta.",
    materials: "Krótki film 10-20 s z ziarnem, jeden mocny kadr 16:9.",
    risk: "Zbyt „agencyjne” dla części klientów; trudniejsze do czytania na mobile.",
    Preview: Manifest,
  },
  {
    id: "dokument",
    name: "Dokument",
    tagline: "Film na pełnym ekranie, tekst na obrazie",
    modern: "Materiał wideo jako pierwszy ekran; ludzka, niedoskonała faktura zamiast grafik AI.",
    materials: "Sesja wideo (wystąpienie, sesja, kulisy) + 2-3 zdjęcia szerokie.",
    risk: "Wymaga naprawdę dobrego wideo; słaby materiał zniszczy cały efekt.",
    Preview: Dokument,
  },
];
